// import { ListingTemplateModel } from '../models/ListingTemplateModel';
import { ListingTemplateModel } from "@/app/models/ListingTemplateModel";
import { useState, useEffect, useRef, useMemo } from "react";

// Global state to track which template is currently auto-scrolling
let activeScrollingTemplateId: string | null = null;

// Helper function to calculate distance from viewport center
function getDistanceFromCenter(element: HTMLElement): number {
  const rect = element.getBoundingClientRect();
  const viewportCenter = window.innerHeight / 2;
  const elementCenter = rect.top + rect.height / 2;
  return Math.abs(viewportCenter - elementCenter);
}

// Helper function to convert Cloudinary image URLs to AVIF format
function convertToAvif(imageUrl: string): string {
  if (!imageUrl || typeof imageUrl !== "string") {
    return imageUrl;
  }

  let optimizedUrl = imageUrl;

  // // Add q_auto if not present
  // if (
  //   optimizedUrl.includes("/upload/") &&
  //   !optimizedUrl.includes("/upload/q_auto")
  // ) {
  //   optimizedUrl = optimizedUrl.replace("/upload/", "/upload/q_auto/");
  // }

  // // Convert .jpg to .avif
  // if (optimizedUrl.endsWith(".jpg") || optimizedUrl.includes(".jpg")) {
  //   optimizedUrl = optimizedUrl.replace(".jpg", ".avif");
  // }

  return optimizedUrl;
}

interface WeddingCardProps {
  template: ListingTemplateModel;
  isPopular?: boolean;
  index?: number;
  onCardClick: (template: ListingTemplateModel) => void;
}

export default function WeddingCard({
  template,
  isPopular = false,
  index,
  onCardClick,
}: WeddingCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hoverInterval, setHoverInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const [scrollInterval, setScrollInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Get all available images and convert to AVIF format
  const rawImages = useMemo(() => {
    return template.images && template.images.length > 0
      ? template.images
      : [template.img_link];
  }, [template.images, template.img_link]);

  // Convert images to AVIF format for better performance
  const images = useMemo(() => {
    return rawImages.map(convertToAvif);
  }, [rawImages]);

  // Also convert img_link for video templates
  const optimizedImgLink = useMemo(() => {
    return convertToAvif(template.img_link);
  }, [template.img_link]);

  // Debug: Log images on render
  useEffect(() => {
    console.log(
      "Template images array:",
      images,
      "Count:",
      images.length,
      "Template:",
      template.id
    );
  }, [images, template.id]);

  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onCardClick(template);
  };

  // Handle mouse enter - start cycling through images
  const handleMouseEnter = () => {
    console.log("Mouse enter - images:", images, "length:", images.length);

    if (images.length <= 1) {
      console.log("Only one image, skipping cycle");
      return; // Don't cycle if only one image
    }

    // Change to next image immediately on hover
    setCurrentImageIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % images.length;
      console.log("Changing image from", prevIndex, "to", nextIndex);
      return nextIndex;
    });

    // Then continue cycling
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % images.length;
        console.log("Cycling image from", prevIndex, "to", nextIndex);
        return nextIndex;
      });
    }, 800); // Change image every 800ms

    setHoverInterval(interval);
  };

  // Handle mouse leave - reset to first image
  const handleMouseLeave = () => {
    if (hoverInterval) {
      clearInterval(hoverInterval);
      setHoverInterval(null);
    }
    setCurrentImageIndex(0);
  };

  // Auto-scroll on mobile when card comes into view (only for non-video templates)
  useEffect(() => {
    // Skip if video template or only one image
    if (template.isVideo || images.length <= 1) return;

    let currentScrollInterval: NodeJS.Timeout | null = null;
    const templateId = template.id;

    const checkAndStartScrolling = () => {
      // Only on mobile
      if (
        window.innerWidth >= 768 ||
        !imageContainerRef.current ||
        !scrollContainerRef.current
      ) {
        return;
      }

      // Check if this is the center-most template
      const allTemplates = document.querySelectorAll("[data-template-id]");
      let closestTemplate: HTMLElement | null = null;
      let minDistance = Infinity;

      allTemplates.forEach((template) => {
        const element = template as HTMLElement;
        const rect = element.getBoundingClientRect();
        // Check if fully visible (100%)
        if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
          const distance = getDistanceFromCenter(element);
          if (distance < minDistance) {
            minDistance = distance;
            closestTemplate = element;
          }
        }
      });

      // Only start scrolling if this is the center-most template and no other is scrolling
      if (
        closestTemplate === imageContainerRef.current &&
        activeScrollingTemplateId === null
      ) {
        activeScrollingTemplateId = templateId;
        let scrollIndex = 0;
        currentScrollInterval = setInterval(() => {
          if (scrollContainerRef.current) {
            scrollIndex = (scrollIndex + 1) % images.length;
            const scrollWidth = scrollContainerRef.current.scrollWidth;
            const itemWidth = scrollWidth / images.length;
            scrollContainerRef.current.scrollTo({
              left: scrollIndex * itemWidth,
              behavior: "smooth",
            });
          }
        }, 2000); // Scroll every 2 seconds (slower)
        setScrollInterval(currentScrollInterval);
      } else if (
        activeScrollingTemplateId === templateId &&
        closestTemplate !== imageContainerRef.current
      ) {
        // Stop if this template is no longer the center-most
        if (currentScrollInterval) {
          clearInterval(currentScrollInterval);
          currentScrollInterval = null;
          setScrollInterval(null);
        }
        activeScrollingTemplateId = null;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio === 1) {
            // 100% visible - check and potentially start scrolling
            setTimeout(checkAndStartScrolling, 100); // Small delay to ensure all observers have fired
          } else {
            // Stop scrolling when not fully visible
            if (currentScrollInterval) {
              clearInterval(currentScrollInterval);
              currentScrollInterval = null;
              setScrollInterval(null);
            }
            // Release the active scrolling slot if this template was active
            if (activeScrollingTemplateId === templateId) {
              activeScrollingTemplateId = null;
            }
          }
        });
      },
      { threshold: 1.0 } // Trigger only when 100% of the card is visible
    );

    // Also check on scroll to update which template should scroll
    const handleScroll = () => {
      if (
        activeScrollingTemplateId === templateId ||
        activeScrollingTemplateId === null
      ) {
        checkAndStartScrolling();
      }
    };

    if (imageContainerRef.current) {
      observer.observe(imageContainerRef.current);
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      if (imageContainerRef.current) {
        observer.unobserve(imageContainerRef.current);
      }
      window.removeEventListener("scroll", handleScroll);
      if (currentScrollInterval) {
        clearInterval(currentScrollInterval);
      }
      // Release the active scrolling slot if this template was active
      if (activeScrollingTemplateId === templateId) {
        activeScrollingTemplateId = null;
      }
    };
  }, [images.length, template.isVideo, template.id]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (hoverInterval) {
        clearInterval(hoverInterval);
      }
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [hoverInterval, scrollInterval]);

  // Create a title for the image alt text
  const imageAltText = `${template.title} wedding invitation template`;

  return (
    <div className="flex flex-col cursor-pointer" onClick={handleCardClick}>
      {/* Image container with background color */}
      <div className="relative overflow-hidden border border-[#eaeaea] mb-1">
        {/* Page count badge - only show for non-video templates on web */}
        {!template.isVideo && (
          <>
            <div
              className="hidden md:block absolute bottom-8 left-8 px-2 rounded-full z-10 flex items-center justify-center text-center"
              style={{
                backgroundColor: "#AEF6FF",
                color: "#222222",
                fontSize: "14px",
                fontWeight: 600,
                height: "28px",
                lineHeight: "28px",
                textAlign: "center",
              }}
            >
              {template.pages === 1 ? "1 page" : `${template.pages} pages`}
            </div>
            {/* Three dots badge - only show on mobile web for non-video templates */}
            <div
              className="block md:hidden absolute bottom-[14px] left-1/2 -translate-x-1/2 rounded-full z-10 flex flex-row items-center justify-center gap-1 px-1"
              style={{
                backgroundColor: "#000000",
                height: "20px",
              }}
            >
              <div className="w-1 h-1 rounded-full bg-white"></div>
              <div className="w-1 h-1 rounded-full bg-white"></div>
              <div className="w-1 h-1 rounded-full bg-white"></div>
            </div>
          </>
        )}
        {/* Video badge - only show for video templates on web */}
        {template.isVideo && (
          <div
            className="absolute bottom-[14px] md:bottom-8 left-1/2 -translate-x-1/2 md:left-8 md:translate-x-0 rounded-full z-10 flex flex-row items-center justify-center px-2 md:px-3 h-[20px] md:h-[28px] whitespace-nowrap"
            style={{
              backgroundColor: "#FE406C",
            }}
          >
            {/* Play icon */}
            <svg
              className="w-[8px] h-[10px] md:w-[10px] md:h-[12px] mr-1 md:mr-1.5 flex-shrink-0"
              viewBox="0 0 10 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0 0L10 6L0 12V0Z" fill="white" />
            </svg>
            {/* Duration text */}
            <span className="text-white font-semibold text-[10px] md:text-[14px]">
              {template.pages}&nbsp;Slides
            </span>
          </div>
        )}
        {/* Popular tag */}
        {/* {isPopular && (
          <div className="absolute top-0 left-0 bg-[#AA134C] text-white font-[600] text-[12px] font-semibold px-3 py-1 z-10">
            Popular
          </div>
        )} */}
        <div className="w-full p-[10px] md:p-6">
          {/* Desktop: Single image view */}
          <div className="hidden md:block relative w-full aspect-[595/842] bg-gray-100 overflow-visible p-3">
            <img
              key={`${template.id}-${currentImageIndex}`}
              src={
                template.isVideo ? optimizedImgLink : images[currentImageIndex]
              }
              alt={imageAltText}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ boxShadow: "0 0 10px 0 rgba(39, 41, 59, 0.25)" }}
              loading="lazy" //"lazy"
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>
          {/* Mobile: Scrollable horizontal container (only for non-video templates) */}
          {!template.isVideo && (
            <div
              ref={imageContainerRef}
              data-template-id={template.id}
              className="block md:hidden relative w-full aspect-[595/842] bg-gray-100 overflow-hidden"
            >
              <div
                ref={scrollContainerRef}
                className="flex h-full overflow-x-auto scrollbar-hide snap-x snap-mandatory"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {images.map((image: string, index: number) => (
                  <div
                    key={`${template.id}-${index}`}
                    className="flex-shrink-0 w-full h-full snap-center relative"
                  >
                    <img
                      src={image}
                      alt={`${imageAltText} - Page ${index + 1}`}
                      className="w-full h-full object-cover"
                      style={{ boxShadow: "0 0 10px 0 rgba(39, 41, 59, 0.25)" }}
                      loading="lazy"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Mobile: Single image for video templates (no scrolling) */}
          {template.isVideo && (
            <div className="block md:hidden relative w-full aspect-[595/842] bg-gray-100 overflow-hidden">
              <img
                src={optimizedImgLink}
                alt={imageAltText}
                className="w-full h-full object-cover"
                style={{ boxShadow: "0 0 10px 0 rgba(39, 41, 59, 0.25)" }}
                loading="lazy"
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          )}
        </div>
      </div>

      {/* Title and price section */}
      <div className="bg-white text-left rounded-b-lg">
        <p className="text-[#0B214A] text-[14px] font-[400] md:font-[500] truncate mb-1 text-left">
          {template.title}
        </p>
        <div className="flex items-center space-x-2 justify-start">
          <span className="text-[#0B214A] text-[14px] font-[700]">
            {template.culture === "marathi"
              ? `₹${template.price}`
              : `₹${template.price}`}
          </span>
          {template.isPaid && (
            <span className="text-[#F1AEAE]  font-[700] text-[12px] line-through text-sm">
              ₹{template.originalPrice}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
